import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import { firstValueFrom } from 'rxjs';
import {map} from 'rxjs/operators';
import {take} from 'rxjs/operators';
import {ConfigService} from './config/config.service';
import {LocaleService} from './locale.service';
import { Injector } from '@angular/core';
import { AppStorageService } from './app-storage.service';
import { StorageKeys } from './storage-keys';

/**
 * Servicio para gestionar la configuración de traducciones de la aplicación.
 * Proporciona métodos para obtener y establecer el idioma, así como para traducir elementos individuales.
 */
@Injectable({
  providedIn: 'root'
})
export class TranslateConfigService {

  constructor(
    private translate: TranslateService,
    private configService: ConfigService,
  private localeService: LocaleService,
    private injector: Injector,
    private storage: AppStorageService,
  ) {
    // initLanguage is called from AppComponent during app startup to avoid
    // performing async I/O in the service constructor which can block DI.
  }

  /**
   * Inicializa el idioma de la aplicación basado en la configuración guardada
   * o en el idioma del navegador si no hay configuración previa.
   */
  async initLanguage(): Promise<void> {
    // Establece español como idioma de fallback
    this.translate.setDefaultLang('es');

    // Intenta obtener el idioma guardado
    const savedLang = await this.configService.getSelectedLang();

    if (savedLang && savedLang.value) {
      // Si hay un idioma guardado, lo usa
      await firstValueFrom(this.translate.use(savedLang.value));
      this.localeService.setLocale(savedLang.value);
      // Re-sincroniza el idioma con el servidor en cada arranque si el usuario
      // está autenticado. Esto corrige `user.language` aunque un PATCH anterior
      // fallara o el idioma se cambiara estando desconectado, evitando que correos
      // y notificaciones server-to-server (p. ej. confirmación de pago vía webhook)
      // salgan en el idioma equivocado.
      this.persistLanguageToServer(savedLang.value);
    } else {
      // Si no hay idioma guardado, intenta usar el del navegador o español
      const browserLang = this.translate.getBrowserLang();
      const langCodes = this.configService.langs.map(lang => lang.value);

      // Usa el idioma del navegador solo si está soportado
      if (browserLang && langCodes.includes(browserLang)) {
  await firstValueFrom(this.translate.use(browserLang));
  this.configService.setLang(browserLang);
  this.localeService.setLocale(browserLang);
      } else {
        // Por defecto usa español
  await firstValueFrom(this.translate.use('es'));
  this.configService.setLang('es');
  this.localeService.setLocale('es');
      }
    }
  }

  /**
   * Obtiene el idioma predeterminado del navegador y lo establece como idioma predeterminado.
   * @returns {string} El código del idioma detectado en el navegador
   */
  getDefaultLanguage(): string {
    const language: string = this.translate.getBrowserLang() || 'es';
    this.translate.setDefaultLang(language);
    return language;
  }

  /**
   * Establece el idioma activo de la aplicación.
   * @param {string} setLang - El código del idioma a establecer
   */
  setLanguage(setLang: string, persistToServer: boolean = true): void {
  void firstValueFrom(this.translate.use(setLang));
  this.configService.setLang(setLang);
  // inform locale service immediately
  try { this.localeService.setLocale(setLang); } catch (e) { /* ignore */ }

    // Persist language server-side if the caller allows it.
    if (!persistToServer) return;
    this.persistLanguageToServer(setLang);
  }

  /**
   * Envía el idioma elegido al backend (PATCH /profiles/language) si el usuario
   * está autenticado, para mantener `user.language` sincronizado. Los correos y
   * notificaciones se localizan con ese campo, así que un fallo aquí provocaría
   * mensajes en el idioma equivocado: por eso los errores se registran en vez de
   * silenciarse.
   *
   * Se infiere la autenticación desde el token/guest en almacenamiento para
   * evitar ciclos de DI (ProfileService -> HttpService -> TranslateConfigService);
   * ProfileService se resuelve de forma perezosa por el mismo motivo.
   */
  private persistLanguageToServer(lang: string): void {
    try {
      const token = this.storage.get(StorageKeys.TOKEN);
      const guest = this.storage.get(StorageKeys.GUEST);
      if (!token || guest === 'true') {
        return;
      }

      (async () => {
        try {
          const mod = await import('./api/profile.service');
          const ProfileServiceClass = (mod as any).ProfileService;
          if (!ProfileServiceClass) {
            return;
          }
          const profileService = this.injector.get(ProfileServiceClass);
          profileService.updateLanguage(lang).subscribe({
            next: () => {},
            error: (err: any) => {
              console.warn('[i18n] No se pudo sincronizar el idioma con el servidor', err);
            }
          });
        } catch (e) {
          console.warn('[i18n] No se pudo resolver ProfileService para sincronizar el idioma', e);
        }
      })();
    } catch (e) {
      console.warn('[i18n] Error inesperado al sincronizar el idioma con el servidor', e);
    }
  }

  /**
   * Obtiene la traducción de un elemento específico.
   * @param {string} item - La clave de traducción a obtener
   * @returns {string} El texto traducido
   */
  getItemTranslated(item: string, params?: Record<string, any>): string {
    // Prefer synchronous access using instant; this returns the currently loaded
    // translation (or the key if not available). This avoids race conditions
    // where callers expect a string immediately during component init.
    try {
      const instant = this.translate.instant(item, params);
      if (instant !== undefined && instant !== null) {
        return instant as string;
      }
    } catch (e) {
      // fall through to async retrieval below if instant fails
    }

    // As a fallback, attempt a synchronous value via a short-lived subscription.
    // Note: this will likely return the key when translations are not yet loaded.
    let msg = '';
    this.translate.get(item, params).pipe(take(1)).subscribe((res: string) => {
      msg = res;
    });
    return msg;
  }

  /**
   * Obtiene la traducción de un elemento de forma asíncrona.
   * @param {string} item - La clave de traducción a obtener
   * @returns {Observable<string>} Observable con el texto traducido
   */
  getItemTranslatedAsync(item: string, params?: Record<string, any>): Observable<string> {
    return this.translate.get(item, params).pipe(take(1));
  }

  /**
   * Obtiene el idioma actual configurado en la aplicación.
   * @returns {string} El código del idioma actual
   */
  getCurrentLanguage(): string {
    return this.translate.currentLang || this.translate.getDefaultLang() || 'en';
  }

  /**
   * Observable que emite cuando cambia el idioma activo.
   * Útil para refrescar datos dependientes del idioma (por ejemplo, nombres de categorías).
   */
  onLanguageChange(): Observable<string> {
    return this.translate.onLangChange.pipe(map((e: any) => e.lang));
  }
}
